import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import authService from '../services/authService';
import socketService from '../services/socketService';
import api from '../services/api';
import {
  getAccounts,
  getAccountToken,
  getActiveAccountId,
  upsertAccount,
  setAccountToken,
  removeAccount as removeStoredAccount,
  removeAccountsForDeletedOrganization,
  setActiveAccount,
  clearAllAccounts,
  ACCOUNTS_KEY,
  ACTIVE_KEY,
  TOKENS_KEY,
} from '../utils/accountStorage';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accounts, setAccounts] = useState(() => getAccounts());

  useEffect(() => {
    const onStorage = (e) => {
      if (
        e.key === ACCOUNTS_KEY ||
        e.key === TOKENS_KEY ||
        e.key === ACTIVE_KEY
      ) {
        setAccounts(getAccounts());
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      const legacy = localStorage.getItem('token');
      if (legacy) {
        try {
          await authService.syncSessionFromBearer(legacy);
        } catch {
          /* ignore */
        }
        localStorage.removeItem('token');
      }

      const tryRestore = async () => {
        const userData = await authService.getCurrentUser();
        setUser(userData);
        upsertAccount(userData);
        if (!getAccountToken(userData._id)) {
          try {
            const ex = await api.get('/auth/export-access-token');
            if (ex?.data?.token) {
              setAccountToken(userData._id, ex.data.token);
            }
          } catch {
            /* ignore */
          }
        }
        setAccounts(getAccounts());
        socketService.connect();
        return true;
      };

      try {
        await tryRestore();
      } catch (error) {
        console.error('Failed to fetch current user:', error);
        const errorCode = error?.error?.code;
        const activeId = getActiveAccountId();

        if (errorCode === 'ORGANIZATION_DELETED' && activeId) {
          removeStoredAccount(activeId);
          setAccounts(getAccounts());
        }

        const stored = activeId ? getAccountToken(activeId) : null;
        if (stored && errorCode !== 'ORGANIZATION_DELETED') {
          try {
            await authService.syncSessionFromBearer(stored);
            await tryRestore();
          } catch {
            localStorage.removeItem('user');
          }
        } else {
          localStorage.removeItem('user');
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = useCallback(async (email, password) => {
    const response = await authService.login(email, password);
    setUser(response.data);

    if (response.token) {
      upsertAccount(response.data, response.token);
    } else {
      upsertAccount(response.data);
      try {
        const ex = await api.get('/auth/export-access-token');
        if (ex?.data?.token) {
          setAccountToken(response.data._id, ex.data.token);
        }
      } catch {
        /* ignore */
      }
    }
    setAccounts(getAccounts());
    socketService.connect();

    return response;
  }, []);

  const submitInquiry = useCallback(async (data) => {
    const response = await authService.submitInquiry(data);
    return response;
  }, []);

  const logout = useCallback(async (options = {}) => {
    const { purgeOrganization } = options;
    const currentId = user?._id;

    if (purgeOrganization) {
      removeAccountsForDeletedOrganization(purgeOrganization);
    } else if (currentId) {
      removeStoredAccount(currentId);
    }

    socketService.disconnect();
    setUser(null);
    setAccounts(getAccounts());

    const remaining = getAccounts().filter((account) =>
      Boolean(getAccountToken(account.userId)),
    );

    if (remaining.length > 0) {
      const next = remaining[0];
      const nextTok = getAccountToken(next.userId);
      localStorage.removeItem('currentLocationId');
      setActiveAccount(next.userId);
      try {
        if (!nextTok) {
          await authService.logout().catch(() => {});
          localStorage.removeItem('user');
          return { switched: false };
        }
        await authService.syncSessionFromBearer(nextTok);
        const userData = await authService.getCurrentUser();
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
        upsertAccount(userData);
        setAccounts(getAccounts());
        socketService.connect();
        return { switched: true };
      } catch {
        localStorage.removeItem('user');
        removeStoredAccount(next.userId);
        setAccounts(getAccounts());
        await authService.logout().catch(() => {});
        return { switched: false };
      }
    }

    await authService.logout().catch(() => {});
    return { switched: false };
  }, [user]);

  const forceLogout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('currentLocationId');
    socketService.disconnect();
    setUser(null);
  }, []);

  /** Log out of ALL accounts at once. */
  const logoutAll = useCallback(async () => {
    await authService.logout().catch(() => {});
    socketService.disconnect();
    clearAllAccounts();
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('currentLocationId');
    setUser(null);
    setAccounts([]);
  }, []);

  /** Add another account (login without leaving current session). Returns the new accounts list. */
  const addAccount = useCallback(async (email, password) => {
    const currentUser = user;

    try {
      if (currentUser?._id) {
        try {
          const ex = await api.get('/auth/export-access-token');
          if (ex?.data?.token) {
            setAccountToken(currentUser._id, ex.data.token);
          }
        } catch {
          /* ignore */
        }
      }
      const response = await authService.login(email, password, { skipSessionCookie: true });
      if (response.token) {
        upsertAccount(response.data, response.token);
      }

      if (currentUser) {
        localStorage.setItem('user', JSON.stringify(currentUser));
      }

      const updated = getAccounts();
      setAccounts(updated);
      return updated;
    } catch (error) {
      if (currentUser) {
        localStorage.setItem('user', JSON.stringify(currentUser));
      }
      throw error;
    }
  }, [user]);

  /** Switch to a different saved account. */
  const switchAccount = useCallback(async (userId) => {
    const account = setActiveAccount(userId);
    if (!account) throw new Error('Account not found');
    if (!account.token) {
      removeStoredAccount(userId);
      setAccounts(getAccounts());
      throw new Error('Session expired; please sign in again.');
    }

    socketService.disconnect();

    localStorage.removeItem('currentLocationId');

    try {
      await authService.syncSessionFromBearer(account.token);
      const userData = await authService.getCurrentUser();
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
      upsertAccount(userData);
      setAccounts(getAccounts());
      socketService.connect();
      return userData;
    } catch (error) {
      removeStoredAccount(userId);
      setAccounts(getAccounts());
      throw error;
    }
  }, []);

  /** Remove a saved account (not the active one). */
  const removeAccountById = useCallback((userId) => {
    removeStoredAccount(userId);
    setAccounts(getAccounts());
  }, []);

  const updateUser = useCallback((updatedUserData) => {
    setUser(updatedUserData);
    localStorage.setItem('user', JSON.stringify(updatedUserData));
    upsertAccount(updatedUserData);
    setAccounts(getAccounts());
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const userData = await authService.getCurrentUser();
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
      upsertAccount(userData);
      setAccounts(getAccounts());
      return userData;
    } catch (error) {
      console.error('Failed to refresh user:', error);
      throw error;
    }
  }, []);

  const value = useMemo(() => ({
    user,
    login,
    submitInquiry,
    logout,
    logoutAll,
    forceLogout,
    updateUser,
    refreshUser,
    isAuthenticated: !!user,
    loading,
    // Multi-account
    accounts,
    addAccount,
    switchAccount,
    removeAccount: removeAccountById,
  }), [user, loading, login, submitInquiry, logout, logoutAll, forceLogout, updateUser, refreshUser, accounts, addAccount, switchAccount, removeAccountById]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export default AuthContext;
